
import { computed, defineComponent, onBeforeMount, ref } from "vue";
import { useStore } from "vuex";
import IResponseData from "@/types/IResponseData";

import TravelService from "./services/travel.service";

import FilterOverview from "@/components/FilterOverview.vue";
import DotSpinner from "@/components/EasySpinners/DotSpinner.vue";
import IconSpinner from "./components/EasySpinners/IconSpinner.vue";
import GifSpinner from "./components/EasySpinners/GifSpinner.vue";
import TravelList from "@/components/TravelList/TravelList.vue";
import TravelSorting from "./components/TravelList/TravelSorting.vue";
import {
  SearchFilter,
  SearchTravelRequest,
  SearchTravelResponse,
  SearchTravelSortItem,
} from "./types/models";
import { MultiRangeValues, ValueIndexParams } from "./types/paramModels";
import applicationService from "./services/application.service";

export default defineComponent({
  name: "FilterApp",
  components: {
    FilterOverview,
    TravelList,
    IconSpinner,
    DotSpinner,
    GifSpinner,
    TravelSorting
  },
  setup() {
    const store = useStore();
    let hasResponseError = ref(false);
    let responseErrorMessage = ref(null);

    const pageTexts = computed(() => store.getters["ApplicationModule/getPageTexts"]);
    const localeStrings = computed(() => store.getters["ApplicationModule/getLocaleStrings"]);

    const searchTravelResponse = computed(
      () =>
        store.getters[
        "TravelSearchResponseModule/getSearchTravelResponse"
        ] as SearchTravelResponse
    );

    const searchTravelRequest = computed(
      () =>
        store.getters["TravelSearchModule/getSearchTravelRequest"] as SearchTravelRequest
    );

    const hasNoRequest = computed(() => {
      return !searchTravelRequest.value.dateFrom ||
        !searchTravelRequest.value.airports ||
        !searchTravelRequest.value.rooms[0].adults.length;
    })

    let isResultListLoading = ref(true);
    let isFilterLoading = ref(true);
    let isPageLoading = ref(false);

    const filterHeadline = computed(() => {
      return searchTravelResponse.value.paging.totalResults > 1
        ? pageTexts.value['hotelListPageSearchResultHeadline'].replace(/\[[hotelcount\]]+\]/g, searchTravelResponse.value.paging.totalResults).replace(/\[[destination\]]+\]/g, pageTexts.value['destination'])
        : pageTexts.value['hotelListPageSearchResultHeadlineSingleResult'].replace(/\[[hotelcount\]]+\]/g, searchTravelResponse.value.paging.totalResults).replace(/\[[destination\]]+\]/g, pageTexts.value['destination']);
    })

    const showMoreTravels = () => {
      if (
        searchTravelResponse.value.paging.pageNumber <
        searchTravelResponse.value.paging.numberOfPages
      ) {
        isPageLoading.value = true;
        store.dispatch("TravelSearchResponseModule/incrementPaging").then(() => {
          const filterRequest = {
            paging: searchTravelResponse.value.paging,
            searchFilter: searchTravelResponse.value.searchFilter,
            searchKey: searchTravelResponse.value.searchKey,
            sorting: searchTravelResponse.value.sorting,
          } as SearchTravelResponse;
          TravelService.getFilterSearchTravelResponse(filterRequest).then(
            (response: IResponseData<SearchTravelResponse>) => {
              if (response.data.success) {
                store.dispatch(
                  "TravelSearchResponseModule/addPageTravels",
                  response.data.result.travels
                );
                isPageLoading.value = false;
              } else {
                hasResponseError.value = true;
                responseErrorMessage.value = response.data.exception.message;
              }
            }
          );
        });
      }
    };

    const updateSearchTravelResponse = () => {
      hasResponseError.value = false;
      isResultListLoading.value = true;
      const filterRequest = {
        paging: searchTravelResponse.value.paging,
        searchFilter: searchTravelResponse.value.searchFilter,
        searchKey: searchTravelResponse.value.searchKey,
        sorting: searchTravelResponse.value.sorting
      } as SearchTravelResponse;
      TravelService.getFilterSearchTravelResponse(filterRequest).then(
        (response: IResponseData<SearchTravelResponse>) => {
          if (response.data.success) {
            store.dispatch(
              "TravelSearchResponseModule/updateSearchTravelResponse",
              response.data.result
            );
            isResultListLoading.value = false;
          } else {
            hasResponseError.value = true;
            responseErrorMessage.value = response.data.exception.message;
          }
        }
      );
    };

    const updateSort = (sortItem: SearchTravelSortItem) => {
      store.dispatch("TravelSearchResponseModule/updateSorting", sortItem).then(() => {
        updateSearchTravelResponse();
      });
    };

    const updateFilters = (searchFilter: SearchFilter) => {
      store
        .dispatch("TravelSearchResponseModule/updateFilters", searchFilter)
        .then(() => {
          updateSearchTravelResponse();
        });
    };

    const updateFilterStars = (params: ValueIndexParams) => {
      store.dispatch("TravelSearchResponseModule/updateFilterStars", params).then(() => {
        updateSearchTravelResponse();
      });
    };

    const updateFilterCategories = (params: ValueIndexParams) => {
      store
        .dispatch("TravelSearchResponseModule/updateFilterCategories", params)
        .then(() => {
          updateSearchTravelResponse();
        });
    };

    const updateFilterMinimumPrice = (minPrice: number) => {
      store
        .dispatch("TravelSearchResponseModule/updateFilterMinimumPrice", minPrice)
        .then(() => {
          updateSearchTravelResponse();
        });
    };

    const updateFilterMaximumPrice = (maxPrice: number) => {
      store
        .dispatch("TravelSearchResponseModule/updateFilterMaximumPrice", maxPrice)
        .then(() => {
          updateSearchTravelResponse();
        });
    };

    const updateFilterPriceRange = (range: MultiRangeValues) => {
      store
        .dispatch("TravelSearchResponseModule/updateFilterPriceRange", range)
        .then(() => {
          updateSearchTravelResponse();
        });
    };

    const updateFilterQuery = (query: string) => {
      store.dispatch("TravelSearchResponseModule/updateFilterQuery", query).then(() => {
        updateSearchTravelResponse();
      });
    };

    onBeforeMount(() => {
      if (!store.getters["ApplicationModule/getPageUrls"]) {
        applicationService
          .getPageUrls()
          .then((response: IResponseData<{ [key: string]: string }>) => {
            store.dispatch("ApplicationModule/updatePageUrls", response.data);
          });
      }

      if (!store.getters["ApplicationModule/getPageTexts"]) {
        applicationService
          .getPageTexts()
          .then((response: IResponseData<{ [key: string]: string }>) => {
            store.dispatch(
              "ApplicationModule/updatePageTexts",
              response.data
            );
          });
      }

      store.dispatch(
        "ApplicationModule/updateLocaleStrings",
        document.documentElement.getAttribute('lang')
      );

      store.dispatch("TravelSearchBookingModule/deleteSearchTravelBookingOverview");
      if (!searchTravelResponse.value || !searchTravelResponse.value.travels) {
        TravelService.getSearchTravelResponse(searchTravelRequest.value).then(
          (response: IResponseData<SearchTravelResponse>) => {
            store
              .dispatch(
                "TravelSearchResponseModule/updateSearchTravelResponse",
                response.data.result
              )
              .then(() => {
                if (response.data.success) {
                  isResultListLoading.value = false;
                  isFilterLoading.value = false;

                  if (window.location.href.includes('?')) {
                    const queryString = window.location.search.replace(/^\?/, '');
                    window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
                    updateFilterQuery(decodeURIComponent(queryString));
                  }
                } else {
                  hasResponseError.value = true;
                  responseErrorMessage.value = response.data.exception.message ?? "Kontakt venligst administrator, hvis problemet holder ved.";
                }
              });
          }
        );
      } else {
        isResultListLoading.value = false;
        isFilterLoading.value = false;
      }
    });

    return {
      hasResponseError,
      responseErrorMessage,
      isResultListLoading,
      isFilterLoading,
      searchTravelResponse,
      searchTravelRequest,
      isPageLoading,
      showMoreTravels,
      updateFilterStars,
      updateFilterCategories,
      updateFilterMinimumPrice,
      updateFilterMaximumPrice,
      updateFilterPriceRange,
      updateFilterQuery,
      updateFilters,
      updateSort,
      pageTexts,
      localeStrings,
      filterHeadline,
      hasNoRequest
    };
  },
});
